import { Box, useColorModeValue, Text, Grid } from "@chakra-ui/react";
import React, { useMemo } from "react";
import ProjectLandingSmallTile from "../../project/projectLandingTileLayouts/ProjectLandingSmallTile";
import { useGetConversationCompanyTickerValue, useCollections, useTemporaryProjects, useUserPreference } from "hooks";
import ProjectLandingSmallTilePlaceholder from "../../project/projectLandingTileLayouts/ProjectLandingSmallTilePlaceholder";
import TemporaryProjectSkeletonTile from "../../project/projectLandingTileLayouts/TemporaryProjectSkeletonTile";
import { useCopyValue } from "hooks/useCopies";
import type { PlaceholderCompany } from "types/copy";
import { useNavigate } from "react-router-dom";

interface ProjectWatchListGridProps {
  filterCollectionGroups?: Array<{
    id: string;
    groupName: string;
    groupType: string;
    groupCount: number;
    ticker: string;
  }>;
}
const MAX_PROJECTS = 6;

export const ProjectWatchListGrid: React.FC<ProjectWatchListGridProps> = ({ filterCollectionGroups }) => {
  const showFullWidth = useUserPreference("ui_enable_view_full_width") as boolean;
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const collections = useCollections();
  const placeholderTickersData = useCopyValue("copy_placeholder_tickers");
  const recentCollectionsOwnedByUser = useMemo(
    () =>
      collections.filter((collection) => collection.collectionType === "due_diligence" && !collection.shareDetails).slice(0, MAX_PROJECTS),
    [collections]
  );
  const { temporaryProjects, tickerToLogoMap } = useTemporaryProjects(recentCollectionsOwnedByUser);
  const navigate = useNavigate();

  const placeholderCompanies = useMemo(
    () => placeholderTickersData.map((item) => JSON.parse(item) as PlaceholderCompany),
    [placeholderTickersData]
  );

  const allProjectConversationIds = useMemo(
    () => recentCollectionsOwnedByUser.map((collection) => collection.conversationId).filter((id): id is string => id !== undefined),
    [recentCollectionsOwnedByUser]
  );

  const getConversationTicker = useGetConversationCompanyTickerValue();
  const filteredPlaceholderCompanies = useMemo(() => {
    const stockTickers = new Set(collections.map((collection) => collection.ticker && collection.ticker.toLowerCase()));
    const conversationTickerValues = allProjectConversationIds.flatMap((conversationId) => {
      const maybeValue = getConversationTicker(conversationId);

      if (maybeValue) {
        return [maybeValue];
      } else {
        return [];
      }
    });

    // Get tickers from temporary projects to avoid showing placeholders for them
    const tempProjectTickers = temporaryProjects.map((tempProject) => tempProject.ticker.toLowerCase());
    const allTickers = new Set([...stockTickers, ...conversationTickerValues.map((ticker) => ticker.toLowerCase()), ...tempProjectTickers]);

    return placeholderCompanies.filter((company: PlaceholderCompany) => !allTickers.has(company.ticker.toLowerCase()));
  }, [collections, allProjectConversationIds, placeholderCompanies, getConversationTicker, temporaryProjects]);

  return (
    <Box className="ch-watch-list-grid" border="none" boxShadow="none" width="100%" height="100%">
      <Text
        pb="0"
        fontWeight="600"
        fontSize="md"
        lineHeight="1.4"
        color={textColor}
        mb={2}
        cursor={"pointer"}
        onClick={() => {
          navigate(`/projects/due_diligence`);
        }}>
        Latest Research
      </Text>
      <Grid
        templateColumns={{
          base: "1fr",
          md: showFullWidth ? "repeat(auto-fit, minmax(350px, 1fr))" : "1fr",
          lg: showFullWidth ? "repeat(auto-fit, minmax(350px, 1fr))" : "1fr",
        }}
        gap="1rem"
        alignItems="flex-start"
        width="100%">
        {/* Render temporary skeleton tiles for projects being submitted (only if they don't have a matching real project) */}
        {temporaryProjects
          .filter(
            (tempProject) => !filterCollectionGroups?.some((project) => project?.ticker?.toLowerCase() === tempProject.ticker.toLowerCase())
          )
          .map((tempProject) => (
            <TemporaryProjectSkeletonTile
              key={`temp-skeleton-${tempProject.id}`}
              ticker={tempProject.ticker}
              exchange={tempProject.exchange}
              companyName={tempProject.companyName}
              logoUrl={tempProject.companyLogoUrl}
            />
          ))}
        {filterCollectionGroups?.map((project, index) => {
          if (!project) return null;
          // Find a matching temporary project by ticker (if any)
          const matchingTempProject = temporaryProjects.find(
            (tempProject) => tempProject.ticker.toLowerCase() === project.ticker?.toLowerCase()
          );
          // Use the logo URL from the matching temporary project or from the stored map
          const logoUrl =
            matchingTempProject?.companyLogoUrl || (project.ticker ? tickerToLogoMap[project.ticker.toLowerCase()] : undefined);

          return (
            <ProjectLandingSmallTile
              key={`ch-watch-list-tile-${index}`}
              collectionId={project.id}
              logoUrl={logoUrl}
              companyDescription={matchingTempProject?.companyDescription}
            />
          );
        })}
        {filterCollectionGroups &&
          filterCollectionGroups.length < MAX_PROJECTS &&
          filteredPlaceholderCompanies.map((company) => (
            <ProjectLandingSmallTilePlaceholder
              key={`placeholder-${company.ticker}`}
              companyLogoUrl={company.logo}
              companyName={company.companyName}
              ticker={company.ticker}
              exchange={company.exchange}
              companyDescription={company.overview || undefined}
            />
          ))}
      </Grid>
    </Box>
  );
};
